import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, ChevronDown, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * PrintfulCatalogPicker
 * ---------------------
 * Small picker used inside ProductForm when Fulfillment Type = "Printful Fulfillment".
 * Renders a searchable dropdown of synced Printful sync-products and, once one is
 * chosen, a variants dropdown. Selections auto-populate:
 *   - printful_sync_product_id
 *   - printful_sync_variant_id
 * The parent form always keeps the raw text fields as a manual fallback so admins
 * can paste IDs when the picker is unavailable (e.g., API error, empty catalog).
 *
 * Props:
 *   productId          string     current printful_sync_product_id (may be empty)
 *   variantId          string     current printful_sync_variant_id (may be empty)
 *   onChange           (patch) => void — merges { printful_sync_product_id, printful_sync_variant_id }
 *   onSelectVariant    (variant) => void  optional — surfaces the whole variant blob (price, name)
 */
export default function PrintfulCatalogPicker({ productId, variantId, onChange, onSelectVariant }) {
  const [productsState, setProductsState] = useState({ loading: false, error: null, items: [] });
  const [variantsState, setVariantsState] = useState({ loading: false, error: null, items: [], forProductId: null });
  const [search, setSearch] = useState('');
  const [productsOpen, setProductsOpen] = useState(false);
  const [variantsOpen, setVariantsOpen] = useState(false);

  // Initial fetch — one request, cached in state for the life of the drawer.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setProductsState((s) => ({ ...s, loading: true, error: null }));
      try {
        const { data } = await api.get('/admin/integrations/printful/products');
        if (cancelled) return;
        if (!data?.ok) {
          setProductsState({ loading: false, error: data?.error || 'Printful is unavailable.', items: [] });
          return;
        }
        setProductsState({ loading: false, error: null, items: Array.isArray(data.result) ? data.result : [] });
      } catch (e) {
        if (cancelled) return;
        setProductsState({ loading: false, error: e?.response?.data?.detail || e.message || 'Failed to load catalog', items: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-load variants when productId matches a known product (either
  // user-selected here or pre-filled from an existing record).
  useEffect(() => {
    if (!productId) {
      setVariantsState({ loading: false, error: null, items: [], forProductId: null });
      return;
    }
    if (variantsState.forProductId === productId) return; // already loaded
    let cancelled = false;
    (async () => {
      setVariantsState((s) => ({ ...s, loading: true, error: null, forProductId: productId }));
      try {
        const { data } = await api.get(`/admin/integrations/printful/products/${productId}`);
        if (cancelled) return;
        const result = data?.result || {};
        const variants = Array.isArray(result.sync_variants) ? result.sync_variants : [];
        setVariantsState({ loading: false, error: null, items: variants, forProductId: productId });
      } catch (e) {
        if (cancelled) return;
        setVariantsState({ loading: false, error: e?.response?.data?.detail || 'Failed to load variants', items: [], forProductId: productId });
      }
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [productId]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return productsState.items;
    return productsState.items.filter((p) => (p.name || '').toLowerCase().includes(q) || String(p.id).includes(q));
  }, [productsState.items, search]);

  const selectedProduct = useMemo(
    () => productsState.items.find((p) => String(p.id) === String(productId)) || null,
    [productsState.items, productId]
  );
  const selectedVariant = useMemo(
    () => variantsState.items.find((v) => String(v.id) === String(variantId)) || null,
    [variantsState.items, variantId]
  );

  const chooseProduct = (p) => {
    onChange({
      printful_sync_product_id: String(p.id),
      // Reset variant when product changes.
      printful_sync_variant_id: '',
    });
    setProductsOpen(false);
    setSearch('');
  };

  const chooseVariant = (v) => {
    onChange({ printful_sync_variant_id: String(v.id) });
    if (onSelectVariant) onSelectVariant(v);
    setVariantsOpen(false);
  };

  const refresh = async () => {
    setProductsState({ loading: true, error: null, items: [] });
    try {
      const { data } = await api.get('/admin/integrations/printful/products');
      if (!data?.ok) {
        setProductsState({ loading: false, error: data?.error || 'Printful is unavailable.', items: [] });
        return;
      }
      setProductsState({ loading: false, error: null, items: Array.isArray(data.result) ? data.result : [] });
    } catch (e) {
      setProductsState({ loading: false, error: e?.response?.data?.detail || 'Refresh failed', items: [] });
    }
  };

  const hasCatalog = productsState.items.length > 0;
  const showFallbackHint = !!productsState.error || (!productsState.loading && !hasCatalog);

  return (
    <div className="space-y-3" data-testid="printful-catalog-picker">
      {/* --- Products dropdown --- */}
      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Printful product</label>
          <button
            type="button"
            onClick={refresh}
            className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-2 py-1 text-[10px] font-mono tracking-widest inline-flex items-center gap-1"
            aria-label="Refresh Printful catalog"
            data-testid="pf-picker-refresh"
          >
            <RefreshCw size={11} className={productsState.loading ? 'animate-spin' : ''} /> REFRESH
          </button>
        </div>

        <button
          type="button"
          onClick={() => setProductsOpen((o) => !o)}
          disabled={productsState.loading || (!!productsState.error && !hasCatalog)}
          data-testid="pf-picker-product-select"
          className="w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm flex items-center justify-between disabled:opacity-60"
        >
          <span className="truncate text-left flex-1">
            {selectedProduct ? (
              <span className="inline-flex items-center gap-2">
                {selectedProduct.thumbnail_url && (
                  <img src={selectedProduct.thumbnail_url} alt="" className="w-6 h-6 rounded object-cover" />
                )}
                <span className="text-[var(--pg-ink)]">{selectedProduct.name}</span>
                <span className="text-[10px] font-mono text-[var(--pg-ink-faint)]">#{selectedProduct.id}</span>
              </span>
            ) : productsState.loading ? (
              <span className="text-[var(--pg-ink-muted)]">Loading Printful catalog…</span>
            ) : productId ? (
              <span className="text-[var(--pg-gold-2)] font-mono text-[11px]">Manual ID: {productId}</span>
            ) : (
              <span className="text-[var(--pg-ink-muted)]">Select a synced Printful product…</span>
            )}
          </span>
          <ChevronDown size={14} className="text-[var(--pg-ink-muted)] shrink-0" />
        </button>

        {productsOpen && !productsState.loading && hasCatalog && (
          <div
            className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto pg-scroll bg-[var(--pg-navy-900)] border border-[var(--pg-border-2)] rounded-[var(--pg-radius-sm)] shadow-2xl"
            data-testid="pf-picker-product-dropdown"
          >
            <div className="p-2 border-b border-[var(--pg-border-2)] sticky top-0 bg-[var(--pg-navy-900)]">
              <div className="relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--pg-ink-faint)]" />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search products…"
                  className="w-full pg-input rounded-[var(--pg-radius-sm)] pl-7 pr-2 py-1.5 text-xs"
                  data-testid="pf-picker-search"
                />
              </div>
            </div>
            {filteredProducts.length === 0 ? (
              <p className="p-4 text-xs text-[var(--pg-ink-muted)]">No products match &ldquo;{search}&rdquo;.</p>
            ) : (
              <ul className="divide-y divide-[var(--pg-border-2)]">
                {filteredProducts.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => chooseProduct(p)}
                      data-testid={`pf-picker-product-${p.id}`}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[rgba(25,194,142,0.06)] text-left"
                    >
                      {p.thumbnail_url ? (
                        <img src={p.thumbnail_url} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded bg-[var(--pg-navy-800)] shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[var(--pg-ink)] truncate">{p.name}</p>
                        <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">#{p.id} · {p.variants || 0} variants</p>
                      </div>
                      {String(productId) === String(p.id) && <CheckCircle2 size={13} className="text-[var(--pg-emerald)] shrink-0" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* --- Variants dropdown (only after a product is chosen) --- */}
      {productId && (
        <div className="relative">
          <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Printful variant</label>
          <button
            type="button"
            onClick={() => setVariantsOpen((o) => !o)}
            disabled={variantsState.loading || (!!variantsState.error && variantsState.items.length === 0)}
            data-testid="pf-picker-variant-select"
            className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm flex items-center justify-between disabled:opacity-60"
          >
            <span className="truncate text-left flex-1">
              {selectedVariant ? (
                <span className="inline-flex items-center gap-2">
                  <span className="text-[var(--pg-ink)]">{selectedVariant.name || `Variant ${selectedVariant.id}`}</span>
                  {selectedVariant.retail_price && (
                    <span className="text-[10px] font-mono text-[var(--pg-gold-2)]">${selectedVariant.retail_price} {(selectedVariant.currency || 'usd').toUpperCase()}</span>
                  )}
                  <span className="text-[10px] font-mono text-[var(--pg-ink-faint)]">#{selectedVariant.id}</span>
                </span>
              ) : variantsState.loading ? (
                <span className="text-[var(--pg-ink-muted)]">Loading variants…</span>
              ) : variantId ? (
                <span className="text-[var(--pg-gold-2)] font-mono text-[11px]">Manual ID: {variantId}</span>
              ) : (
                <span className="text-[var(--pg-ink-muted)]">Select a variant…</span>
              )}
            </span>
            <ChevronDown size={14} className="text-[var(--pg-ink-muted)] shrink-0" />
          </button>
          {variantsState.error && (
            <p className="mt-1 text-[10px] font-mono text-[var(--pg-neon-pink)] inline-flex items-center gap-1">
              <AlertTriangle size={10} /> {variantsState.error}
            </p>
          )}

          {variantsOpen && !variantsState.loading && variantsState.items.length > 0 && (
            <div
              className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto pg-scroll bg-[var(--pg-navy-900)] border border-[var(--pg-border-2)] rounded-[var(--pg-radius-sm)] shadow-2xl"
              data-testid="pf-picker-variant-dropdown"
            >
              <ul className="divide-y divide-[var(--pg-border-2)]">
                {variantsState.items.map((v) => (
                  <li key={v.id}>
                    <button
                      type="button"
                      onClick={() => chooseVariant(v)}
                      data-testid={`pf-picker-variant-${v.id}`}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[rgba(25,194,142,0.06)] text-left"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[var(--pg-ink)] truncate">{v.name || `Variant ${v.id}`}</p>
                        <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">
                          #{v.id}{v.retail_price ? ` · $${v.retail_price} ${(v.currency || 'usd').toUpperCase()}` : ''}
                        </p>
                      </div>
                      {String(variantId) === String(v.id) && <CheckCircle2 size={13} className="text-[var(--pg-emerald)] shrink-0" />}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* --- Error / empty catalog hint --- */}
      {productsState.error && (
        <p className="text-[10px] font-mono text-[var(--pg-neon-pink)] inline-flex items-center gap-1">
          <AlertTriangle size={10} /> {productsState.error}
        </p>
      )}
      {showFallbackHint && (
        <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">
          Catalog picker unavailable — paste the IDs manually below.
        </p>
      )}
    </div>
  );
}
